import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import { AuthContext } from '../context/AuthContext';
import { getTrialPlanBanner } from '../utils/planAccess';
import { getEffectiveCompanyId } from '../utils/userCompany';
import { formatDate } from '../utils/locale';
import { useConfirm } from '../context/ConfirmContext';
import LoadingSpinner from './LoadingSpinner';
import './BillingSection.css';

const PaymentMethodForm = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation(['company', 'common']);
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      });

      if (error) {
        setFormError(error.message || t('company:paymentSetupFailed'));
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;

      if (paymentMethodId) {
        await axios.post('/api/billing/payment-methods', { paymentMethodId });
      }

      onSuccess();
    } catch (err) {
      setFormError(formatError(err) || t('company:failedSavePaymentMethod'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="billing-payment-form" onSubmit={handleSubmit}>
      <PaymentElement />
      {formError && <div className="error">{formError}</div>}
      <div className="billing-payment-form-actions">
        <button type="submit" className="btn btn-primary" disabled={!stripe || submitting}>
          {submitting ? t('common:saving') : t('company:saveCard')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
          {t('common:cancel')}
        </button>
      </div>
    </form>
  );
};

const BillingSection = () => {
  const { t, i18n } = useTranslation(['company', 'common', 'superAdmin']);
  const confirm = useConfirm();
  const { fetchUser, user } = useContext(AuthContext);
  const trialBanner = getTrialPlanBanner(user);
  const supportReadOnlyBilling = Boolean(user?.supportReadOnlyBilling);
  const [config, setConfig] = useState(null);
  const [billing, setBilling] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [supportTrialPlan, setSupportTrialPlan] = useState('professional');
  const [supportTrialEndsAt, setSupportTrialEndsAt] = useState('');
  const [supportTrialSaving, setSupportTrialSaving] = useState(false);
  const [supportTrialMessage, setSupportTrialMessage] = useState('');

  const statusLabels = {
    active: t('company:statusActive'),
    past_due: t('company:statusPastDue'),
    incomplete: t('company:statusIncomplete'),
    canceled: t('company:statusCanceled'),
    trialing: t('company:statusTrialing'),
    unpaid: t('company:statusUnpaid')
  };

  const formatPrice = (plan) => (
    plan.price === 0
      ? t('company:freePrice')
      : t('company:pricePerMonth', { price: plan.price })
  );

  const formatBillingDate = (iso) => {
    if (!iso) {
      return null;
    }
    return formatDate(iso, i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const loadBilling = useCallback(async () => {
    const [configRes, billingRes] = await Promise.all([
      axios.get('/api/billing/config'),
      axios.get('/api/billing')
    ]);
    const configData = configRes.data.data;
    setConfig(configData);
    setBilling(billingRes.data.data);
    if (configData.publishableKey) {
      setStripePromise(loadStripe(configData.publishableKey));
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadBilling();
      } catch (err) {
        setError(formatError(err) || t('company:billingUnavailable'));
      } finally {
        setLoading(false);
      }
    })();
  }, [loadBilling, t]);

  useEffect(() => {
    if (!loading && window.location.hash === '#billing') {
      document.getElementById('billing')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loading]);

  useEffect(() => {
    if (!billing?.trialPlan) {
      const date = new Date();
      date.setUTCDate(date.getUTCDate() + 14);
      setSupportTrialEndsAt(date.toISOString().slice(0, 10));
      setSupportTrialPlan('professional');
      return;
    }

    setSupportTrialPlan(billing.trialPlan);
    setSupportTrialEndsAt(billing.trialEndsAt ? new Date(billing.trialEndsAt).toISOString().slice(0, 10) : '');
  }, [billing?.trialPlan, billing?.trialEndsAt]);

  const refreshBilling = async () => {
    await loadBilling();
    if (fetchUser) {
      await fetchUser();
    }
  };

  const openPaymentForm = async () => {
    setError('');
    try {
      const res = await axios.post('/api/billing/setup-intent');
      setClientSecret(res.data.data.clientSecret);
      setShowPaymentForm(true);
    } catch (err) {
      setError(formatError(err) || t('company:failedStartPaymentSetup'));
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentForm(false);
    setClientSecret(null);
    await refreshBilling();
  };

  const handleSubscribe = async (planId) => {
    const subscribedPlanId = billing?.billingPlanId ?? billing?.planId;
    if (planId === subscribedPlanId && !billing?.cancelAtPeriodEnd) {
      return;
    }

    setActionLoading(true);
    setError('');
    try {
      await axios.post('/api/billing/subscribe', { planId });
      await refreshBilling();
    } catch (err) {
      setError(formatError(err) || t('company:failedChangePlan'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSupportTrialSave = async (event) => {
    event.preventDefault();
    const companyId = getEffectiveCompanyId(user);
    if (!companyId) {
      return;
    }

    setSupportTrialSaving(true);
    setSupportTrialMessage('');
    setError('');
    try {
      await axios.put(`/api/companies/${companyId}/trial`, {
        trialPlan: supportTrialPlan,
        trialEndsAt: supportTrialEndsAt
      });
      setSupportTrialMessage(t('company:supportTrialSaved'));
      await refreshBilling();
    } catch (err) {
      setError(formatError(err) || t('company:supportTrialFailed'));
    } finally {
      setSupportTrialSaving(false);
    }
  };

  const handleSupportTrialClear = async () => {
    const companyId = getEffectiveCompanyId(user);
    if (!companyId) {
      return;
    }

    if (!(await confirm(t('company:supportTrialClearConfirm'), { danger: true }))) {
      return;
    }

    setSupportTrialSaving(true);
    setSupportTrialMessage('');
    setError('');
    try {
      await axios.put(`/api/companies/${companyId}/trial`, { trialPlan: null });
      setSupportTrialMessage(t('company:supportTrialCleared'));
      await refreshBilling();
    } catch (err) {
      setError(formatError(err) || t('company:supportTrialFailed'));
    } finally {
      setSupportTrialSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!(await confirm(t('company:confirmCancelSubscription'), { danger: true }))) {
      return;
    }

    setActionLoading(true);
    setError('');
    try {
      await axios.post('/api/billing/cancel');
      await refreshBilling();
    } catch (err) {
      setError(formatError(err) || t('company:failedCancelSubscription'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card billing-section">
        <h2>{t('company:billingTitle')}</h2>
        <LoadingSpinner />
      </div>
    );
  }

  if (!config || !billing) {
    return (
      <div className="card billing-section" id="billing">
        <h2>{t('company:billingTitle')}</h2>
        {error ? <div className="error">{error}</div> : <p>{t('company:billingNotAvailable')}</p>}
      </div>
    );
  }

  const defaultPaymentMethod =
    billing.paymentMethods?.find((pm) => pm.isDefault) || billing.paymentMethods?.[0];
  const status = billing.subscriptionStatus;
  const statusClass =
    status === 'past_due' || status === 'incomplete'
      ? `billing-status-${status}`
      : 'billing-status-active';
  const renewalDate = formatBillingDate(billing.subscriptionCurrentPeriodEnd);
  const billingPlanId = billing.billingPlanId ?? billing.planId;
  const isPaidPlan = billingPlanId !== 'basic';
  const isStripeTrialing = status === 'trialing';
  const readOnlyBilling = supportReadOnlyBilling || Boolean(billing.supportReadOnlyBilling);
  const canCancel =
    !readOnlyBilling
    && isPaidPlan
    && billing.subscriptionStatus !== 'canceled'
    && !billing.cancelAtPeriodEnd;

  return (
    <div className="card billing-section" id="billing">
      <h2>{t('company:billingTitle')}</h2>

      {error && <div className="error">{error}</div>}

      {readOnlyBilling && (
        <div className="billing-banner billing-banner-info" role="status">
          {t('company:supportReadOnlyBillingBanner')}
        </div>
      )}

      {trialBanner && (
        <div className="billing-banner billing-banner-info" role="status">
          <strong>{trialBanner.title}</strong>
          <p>{trialBanner.message}</p>
        </div>
      )}

      {isStripeTrialing && renewalDate && (
        <div className="billing-banner billing-banner-info" role="status">
          <strong>{t('company:stripeTrialBannerTitle')}</strong>
          <p>{t('company:stripeTrialBannerMessage', { date: renewalDate })}</p>
        </div>
      )}

      {status === 'past_due' && (
        <div className="billing-banner billing-banner-warning" role="alert">
          {t('company:billingPastDueBanner')}
        </div>
      )}

      {status === 'incomplete' && (
        <div className="billing-banner billing-banner-warning" role="alert">
          {t('company:billingIncompleteBanner')}
        </div>
      )}

      {billing.cancelAtPeriodEnd && renewalDate && (
        <div className="billing-banner billing-banner-info">
          {t('company:billingCancelScheduled', { date: renewalDate })}
        </div>
      )}

      <div className="billing-summary">
        <div className="billing-summary-row">
          <strong>{t('company:currentPlan')}</strong>
          <span>
            {config.plans.find((p) => p.id === billingPlanId)?.name || billingPlanId}
          </span>
        </div>
        {billing.trialActive && billing.planId !== billingPlanId && (
          <div className="billing-summary-row">
            <strong>{t('company:trialAccess')}</strong>
            <span>{config.plans.find((p) => p.id === billing.planId)?.name || billing.planId}</span>
          </div>
        )}
        {status && (
          <div className="billing-summary-row">
            <strong>{t('company:billingStatus')}</strong>
            <span className={statusClass}>{statusLabels[status] || status}</span>
          </div>
        )}
        {renewalDate && isPaidPlan && (
          <div className="billing-summary-row">
            <strong>
              {isStripeTrialing
                ? t('company:trialEndsOn')
                : billing.cancelAtPeriodEnd
                  ? t('company:accessUntil')
                  : t('company:renewsOn')}
            </strong>
            <span>{renewalDate}</span>
          </div>
        )}
      </div>

      {readOnlyBilling && user?.role === 'super_admin' && (
        <div className="billing-support-trial">
          <h3>{t('company:supportTrialTitle')}</h3>
          <p className="billing-support-trial-help">{t('company:supportTrialHelp')}</p>
          <form className="billing-support-trial-form" onSubmit={handleSupportTrialSave}>
            <div className="form-group">
              <label htmlFor="supportTrialPlan">{t('superAdmin:trialTierLabel')}</label>
              <select
                id="supportTrialPlan"
                value={supportTrialPlan}
                onChange={(event) => setSupportTrialPlan(event.target.value)}
                disabled={supportTrialSaving}
              >
                <option value="professional">{t('superAdmin:planProfessional')}</option>
                <option value="enterprise">{t('superAdmin:planEnterprise')}</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="supportTrialEndsAt">{t('superAdmin:trialEndDateLabel')}</label>
              <input
                id="supportTrialEndsAt"
                type="date"
                value={supportTrialEndsAt}
                onChange={(event) => setSupportTrialEndsAt(event.target.value)}
                disabled={supportTrialSaving}
                required
              />
            </div>
            {supportTrialMessage && <div className="success">{supportTrialMessage}</div>}
            <div className="billing-support-trial-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleSupportTrialClear}
                disabled={supportTrialSaving || !billing.trialActive}
              >
                {t('superAdmin:clearTrial')}
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={supportTrialSaving || !supportTrialEndsAt}
              >
                {supportTrialSaving ? t('superAdmin:savingTrial') : t('superAdmin:saveTrial')}
              </button>
            </div>
          </form>
        </div>
      )}

      {!readOnlyBilling && (
        <>
          <h3>{t('company:paymentMethod')}</h3>
          {!showPaymentForm && (
            <div className="billing-payment-method">
              {defaultPaymentMethod ? (
                <div className="billing-payment-details">
                  {defaultPaymentMethod.brand?.toUpperCase()} •••• {defaultPaymentMethod.last4}
                  {' '}
                  ({t('company:cardExp', {
                    month: defaultPaymentMethod.expMonth,
                    year: defaultPaymentMethod.expYear
                  })})
                </div>
              ) : (
                <div className="billing-payment-details">{t('company:noPaymentMethod')}</div>
              )}
              <button type="button" className="btn btn-secondary btn-sm" onClick={openPaymentForm}>
                {defaultPaymentMethod ? t('company:updateCard') : t('company:addCard')}
              </button>
            </div>
          )}

          {showPaymentForm && clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentMethodForm
                onSuccess={handlePaymentSuccess}
                onCancel={() => {
                  setShowPaymentForm(false);
                  setClientSecret(null);
                }}
              />
            </Elements>
          )}

          <h3>{t('company:changePlan')}</h3>
          <div className="billing-plans-grid">
            {config.plans.map((plan) => {
              const isCurrent = plan.id === billingPlanId && !billing.cancelAtPeriodEnd;
              return (
                <div
                  key={plan.id}
                  className={`billing-plan-card${isCurrent ? ' current' : ''}`}
                >
                  <div className="billing-plan-header">
                    <h3>{plan.name}</h3>
                    <div className="billing-plan-price">{formatPrice(plan)}</div>
                  </div>
                  <p className="billing-plan-description">{plan.description}</p>
                  <ul className="billing-plan-features">
                    {plan.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                  <div className="billing-plan-actions">
                    {isCurrent ? (
                      <span className="billing-plan-current-label">{t('company:currentPlanLabel')}</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={actionLoading}
                        onClick={() => handleSubscribe(plan.id)}
                      >
                        {plan.price === 0
                          ? t('company:switchToBasic')
                          : t('company:switchToPlan', { name: plan.name })}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {canCancel && (
        <div className="billing-cancel-section">
          <p className="billing-cancel-note">
            {t('company:cancelNote')}
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={actionLoading}
            onClick={handleCancel}
          >
            {t('company:cancelSubscription')}
          </button>
        </div>
      )}
    </div>
  );
};

export default BillingSection;
